'use client';
'use strict';

var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var tailwindMerge = require('tailwind-merge');
var Link = require('next/link');
var Typography = require('../../Base/Typography/Typography.js');
var Box = require('../../Base/Layouts/Box/Box.js');
require('../../Base/Layouts/FullBleed/FullBleed.js');

const BoxTabContext = react.createContext({
    activeTab: 0,
    currentActiveTab: 0,
    handleTabClick: (index)=>{}
});
/**
 * BoxTab의 최상위 컴포넌트입니다.
 * 탭의 상태를 관리하고 하위 컴포넌트에 Context를 제공합니다.
 *
 * @param {RootProps} props
 * @param {ReactNode} props.children - 하위 컴포넌트
 * @param {number} [props.currentActiveTab=0] - 현재 활성화된 탭의 인덱스
 * @param {(index: number) => void} [props.onChange] - 탭 변경 시 호출되는 콜백 함수
 *
 * @example
 * ```tsx
 * <BoxTab.Root currentActiveTab={0} onChange={(index) => console.log(index)}>
 *   <BoxTab.Tabs>
 *     <BoxTab.Tab index={0}>탭 1</BoxTab.Tab>
 *     <BoxTab.Tab index={1}>탭 2</BoxTab.Tab>
 *   </BoxTab.Tabs>
 *   <BoxTab.Panel index={0}>패널 1 내용</BoxTab.Panel>
 *   <BoxTab.Panel index={1}>패널 2 내용</BoxTab.Panel>
 * </BoxTab.Root>
 * ```
 */ const Root = ({ children, currentActiveTab = 0, onChange })=>{
    const [activeTab, setActiveTab] = react.useState(currentActiveTab);
    const handleTabClick = (index)=>{
        if (index === activeTab) return;
        onChange === null || onChange === void 0 ? void 0 : onChange(index);
        setActiveTab(index);
    };
    react.useEffect(()=>{
        if (currentActiveTab !== activeTab) {
            setActiveTab(currentActiveTab);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentActiveTab
    ]);
    return jsxRuntime.jsx(BoxTabContext.Provider, {
        value: {
            activeTab,
            currentActiveTab,
            handleTabClick
        },
        children: children
    });
};
/**
 * 탭 버튼들을 감싸는 컨테이너 컴포넌트입니다.
 *
 * @param {ComponentProps<'div'>} props
 * @param {ReactNode} props.children - Tab 컴포넌트들
 * @param {string} [props.className] - 추가 스타일 클래스
 *
 * @example
 * ```tsx
 * <BoxTab.Tabs className="my-4">
 *   <BoxTab.Tab index={0}>탭 1</BoxTab.Tab>
 *   <BoxTab.Tab index={1}>탭 2</BoxTab.Tab>
 * </BoxTab.Tabs>
 * ```
 */ const Tabs = ({ children, className })=>{
    const childrenArray = react.Children.toArray(children);
    return jsxRuntime.jsx(Box, {
        className: tailwindMerge.twJoin('relative block w-full', className),
        "data-testid": "design-system-boxTab--container",
        children: jsxRuntime.jsx(Box, {
            children: jsxRuntime.jsx(Box, {
                className: tailwindMerge.twMerge('inline-flex w-full'),
                children: childrenArray.map((child, index)=>{
                    const childWithOrder = react.cloneElement(child, {
                        isFirst: index === 0,
                        isLast: index === childrenArray.length - 1
                    });
                    return jsxRuntime.jsx(react.Fragment, {
                        children: childWithOrder
                    }, index);
                })
            })
        })
    });
};
/**
 * 개별 탭 버튼 컴포넌트입니다.
 *
 * @param {TabProps} props
 * @param {ReactNode} props.children - 탭 내용
 * @param {number} props.index - 탭의 인덱스
 * @param {string} [props.className] - 추가 스타일 클래스
 * @param {boolean} [props.isFirst] - 첫 번째 탭 여부 (내부적으로 자동 설정)
 * @param {boolean} [props.isLast] - 마지막 탭 여부 (내부적으로 자동 설정)
 * @param {boolean} [props.disabled] - 비활성화 여부
 *
 * @example
 * ```tsx
 * <BoxTab.Tab index={0} disabled={false}>
 *   탭 1
 * </BoxTab.Tab>
 * ```
 */ const Tab = ({ children, index, className, isFirst, isLast, disabled = false, href })=>{
    const { handleTabClick, activeTab } = react.useContext(BoxTabContext);
    const isSelected = index === activeTab;
    const sharedProps = {
        tabIndex: 0,
        'data-testid': `design-system-boxTab--item-${index}`,
        className: tailwindMerge.twMerge('group flex w-full cursor-pointer items-center border border-w-gray-200 bg-w-white px-6 py-4 hover:bg-w-gray-10', 'relative', '-ml-px', isSelected && 'z-[1] border-primary bg-primary-10 hover:bg-primary-10', isFirst && 'ml-0 rounded-l-xl', isLast && 'rounded-r-xl', className),
        onClick: ()=>{
            !disabled && handleTabClick(index);
        }
    };
    const content = jsxRuntime.jsx(Box, {
        className: tailwindMerge.twMerge('relative flex w-full flex-row items-center justify-center font-normal text-w-gray-600', isSelected && 'font-medium text-primary'),
        children: jsxRuntime.jsx(Typography, {
            variant: "body1",
            className: "relative",
            children: children
        })
    });
    if (href) {
        return jsxRuntime.jsx(Link, Object.assign({
            href: href
        }, sharedProps, {
            children: content
        }));
    }
    return jsxRuntime.jsx(Box, Object.assign({}, sharedProps, {
        children: content
    }));
};
/**
 * 탭 패널 컴포넌트입니다.
 * 해당 인덱스의 탭이 선택되었을 때만 내용을 표시합니다.
 *
 * @param {PanelProps} props
 * @param {ReactNode} props.children - 패널 내용
 * @param {number} props.index - 패널의 인덱스 (연결된 탭의 인덱스와 일치해야 함)
 *
 * @example
 * ```tsx
 * <BoxTab.Panel index={0}>
 *   첫 번째 탭의 내용입니다.
 * </BoxTab.Panel>
 * ```
 */ const Panel = ({ children, index })=>{
    const { activeTab } = react.useContext(BoxTabContext);
    return activeTab === index && jsxRuntime.jsx(Box, {
        "data-testid": "design-system-boxTab--content",
        children: children
    });
};

exports.Panel = Panel;
exports.Root = Root;
exports.Tab = Tab;
exports.Tabs = Tabs;
